import { useCallback, useEffect, useMemo, useState } from "react";
import {
  App,
  Button,
  Card,
  Col,
  Collapse,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { queryAuditLog, type AuditLogEntry } from "../../lib/api";
import { ditColors } from "../../lib/theme";

dayjs.extend(relativeTime);

const AUDITED_COLLECTIONS = [
  "projects",
  "publicIntakes",
  "vendorsPublic",
  "userAllowlist",
  "projectStages",
  "projectTasks",
  "taskSubtasks",
  "projectMetrics",
  "scoringRubrics",
  "scoringEvaluations",
  "workflowBriefs",
  "intakeWorkflows",
  "workflowMeetings",
  "settings",
] as const;

const ACTION_OPTIONS = [
  { label: "Create", value: "create" },
  { label: "Update", value: "update" },
  { label: "Delete", value: "delete" },
] as const;

const ACTION_TAG_COLOR: Record<string, string> = {
  create: "green",
  update: "blue",
  delete: "red",
};

type ChannelFilter = NonNullable<Parameters<typeof queryAuditLog>[0]["channels"]>[number];
type ActionFilter = NonNullable<Parameters<typeof queryAuditLog>[0]["actions"]>[number];

interface FilterState {
  actors: string[];
  apexOnly: boolean;
  actions: ActionFilter[];
  collections: string[];
  dateRange: [Dayjs | null, Dayjs | null] | null;
  search: string;
}

const DEFAULT_RANGE = (): [Dayjs, Dayjs] => [dayjs().subtract(7, "day").startOf("day"), dayjs().endOf("day")];

const initialFilters: FilterState = {
  actors: [],
  apexOnly: false,
  actions: [],
  collections: [],
  dateRange: DEFAULT_RANGE(),
  search: "",
};

function formatAbsolute(ts: string | undefined): string {
  if (!ts) return "—";
  const parsed = dayjs(ts);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm:ss [UTC]") : ts;
}

function formatRelative(ts: string | undefined): string {
  if (!ts) return "—";
  const parsed = dayjs(ts);
  return parsed.isValid() ? parsed.fromNow() : ts;
}

function diffPanel(entry: AuditLogEntry) {
  const fields = Object.keys(entry.diff || {}).sort();
  if (fields.length === 0) {
    return (
      <Typography.Text type="secondary">No field-level changes recorded.</Typography.Text>
    );
  }
  return (
    <Space direction="vertical" size="small" style={{ width: "100%" }}>
      {fields.map((field) => {
        const change = entry.diff[field] || { before: null, after: null };
        return (
          <div key={field}>
            <Typography.Text strong style={{ fontFamily: "monospace" }}>
              {field}
            </Typography.Text>
            <Row gutter={12} style={{ marginTop: 4 }}>
              <Col span={12}>
                <Card
                  size="small"
                  styles={{ body: { padding: 8 } }}
                  style={{
                    background: ditColors.scoreRedBg,
                    borderLeft: `4px solid ${ditColors.scoreRedFg}`,
                  }}
                >
                  <Typography.Text
                    style={{
                      color: ditColors.scoreRedFg,
                      fontSize: 11,
                      letterSpacing: 0.4,
                    }}
                  >
                    BEFORE
                  </Typography.Text>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 12,
                      color: ditColors.primary,
                    }}
                  >
                    {JSON.stringify(change.before, null, 2)}
                  </pre>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  size="small"
                  styles={{ body: { padding: 8 } }}
                  style={{
                    background: ditColors.scoreGreenBg,
                    borderLeft: `4px solid ${ditColors.scoreGreenFg}`,
                  }}
                >
                  <Typography.Text
                    style={{
                      color: ditColors.scoreGreenFg,
                      fontSize: 11,
                      letterSpacing: 0.4,
                    }}
                  >
                    AFTER
                  </Typography.Text>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      fontSize: 12,
                      color: ditColors.primary,
                    }}
                  >
                    {JSON.stringify(change.after, null, 2)}
                  </pre>
                </Card>
              </Col>
            </Row>
          </div>
        );
      })}
    </Space>
  );
}

function ChangedFieldChips({ fields }: { fields: string[] }) {
  if (!fields || fields.length === 0) {
    return <Typography.Text type="secondary">—</Typography.Text>;
  }
  const visible = fields.slice(0, 3);
  const overflow = fields.length - visible.length;
  return (
    <Space size={[4, 4]} wrap>
      {visible.map((f) => (
        <Tag key={f} style={{ marginInlineEnd: 0, fontFamily: "monospace", fontSize: 11 }}>
          {f}
        </Tag>
      ))}
      {overflow > 0 && <Tag color="default">+{overflow} more</Tag>}
    </Space>
  );
}

export default function AuditLogTab() {
  const { message } = App.useApp();
  const [filters, setFilters] = useState<FilterState>(initialFilters);
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildQueryParams = useCallback(() => {
    const channels: ChannelFilter[] | undefined = filters.apexOnly ? ["apex"] : undefined;
    const [from, to] = filters.dateRange ?? [null, null];
    return {
      actors: filters.actors.length ? filters.actors : undefined,
      channels,
      actions: filters.actions.length ? filters.actions : undefined,
      collections: filters.collections.length ? filters.collections : undefined,
      dateFrom: from ? from.toISOString() : undefined,
      dateTo: to ? to.toISOString() : undefined,
      search: filters.search.trim() || undefined,
      limit: 200,
    };
  }, [filters]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await queryAuditLog(buildQueryParams());
      setEntries(result.entries);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [buildQueryParams]);

  useEffect(() => {
    fetchEntries();
    // intentionally re-runs when filters change via fetchEntries' deps
  }, [fetchEntries]);

  // Distinct actor emails from the most-recent fetched page (used to seed the
  // actor multiselect). We deduplicate then sort case-insensitively so the
  // dropdown stays alphabetical.
  const actorOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const e of entries) {
      if (e.actorEmail) seen.add(e.actorEmail);
    }
    return Array.from(seen)
      .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((email) => ({ label: email, value: email }));
  }, [entries]);

  const collectionOptions = useMemo(
    () => AUDITED_COLLECTIONS.map((c) => ({ label: c, value: c })),
    []
  );

  const handleCopy = useCallback(
    async (entry: AuditLogEntry) => {
      try {
        await navigator.clipboard.writeText(JSON.stringify(entry, null, 2));
        message.success("Audit entry copied to clipboard");
      } catch {
        message.error("Copy failed — clipboard permissions may be blocked");
      }
    },
    [message]
  );

  const columns = useMemo(
    () => [
      {
        title: "When",
        dataIndex: "ts",
        key: "ts",
        width: 160,
        render: (ts: string) => (
          <Tooltip title={formatAbsolute(ts)}>
            <span>{formatRelative(ts)}</span>
          </Tooltip>
        ),
      },
      {
        title: "Actor",
        dataIndex: "actorEmail",
        key: "actorEmail",
        width: 220,
        render: (email: string, row: AuditLogEntry) => (
          <Space size={4} wrap>
            <Typography.Text>{email || "—"}</Typography.Text>
            {row.actorChannel === "apex" && (
              <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                Apex
              </Tag>
            )}
            {row.actorChannel === "system" && (
              <Tag color="default" style={{ marginInlineEnd: 0 }}>
                System
              </Tag>
            )}
          </Space>
        ),
      },
      {
        title: "Action",
        dataIndex: "action",
        key: "action",
        width: 100,
        render: (action: string) => (
          <Tag color={ACTION_TAG_COLOR[action] || "default"} style={{ textTransform: "capitalize" }}>
            {action}
          </Tag>
        ),
      },
      {
        title: "Doc",
        key: "doc",
        width: 240,
        render: (_: unknown, row: AuditLogEntry) => (
          <div style={{ fontFamily: "monospace", fontSize: 12, lineHeight: 1.3 }}>
            <div>{row.collection}</div>
            <div style={{ color: ditColors.secondaryDark }}>{row.docId}</div>
          </div>
        ),
      },
      {
        title: "Changed fields",
        dataIndex: "changedFields",
        key: "changedFields",
        render: (fields: string[]) => <ChangedFieldChips fields={fields || []} />,
      },
    ],
    []
  );

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Card title="Filters">
        <Form layout="vertical" component="div">
          <Row gutter={[12, 12]}>
            <Col xs={24} md={12} lg={8}>
              <Form.Item label="Actor" htmlFor="audit-actor-select" style={{ marginBottom: 0 }}>
                <Select
                  id="audit-actor-select"
                  mode="multiple"
                  placeholder="All actors (from current page)"
                  value={filters.actors}
                  onChange={(actors) => setFilters((f) => ({ ...f, actors }))}
                  options={actorOptions}
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={4}>
              <Form.Item label="Apex actions only" style={{ marginBottom: 0 }}>
                <Switch
                  checked={filters.apexOnly}
                  onChange={(apexOnly) => setFilters((f) => ({ ...f, apexOnly }))}
                  aria-label="Filter to Apex-initiated actions only"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Form.Item label="Action" htmlFor="audit-action-select" style={{ marginBottom: 0 }}>
                <Select
                  id="audit-action-select"
                  mode="multiple"
                  placeholder="All actions"
                  value={filters.actions}
                  onChange={(actions) =>
                    setFilters((f) => ({ ...f, actions: actions as ActionFilter[] }))
                  }
                  options={ACTION_OPTIONS as unknown as { label: string; value: string }[]}
                  allowClear
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={6}>
              <Form.Item label="Collection" htmlFor="audit-collection-select" style={{ marginBottom: 0 }}>
                <Select
                  id="audit-collection-select"
                  mode="multiple"
                  placeholder="All collections"
                  value={filters.collections}
                  onChange={(collections) => setFilters((f) => ({ ...f, collections }))}
                  options={collectionOptions}
                  allowClear
                  maxTagCount="responsive"
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={10}>
              <Form.Item label="Date range" style={{ marginBottom: 0 }}>
                <DatePicker.RangePicker
                  value={filters.dateRange ?? undefined}
                  onChange={(range) =>
                    setFilters((f) => ({
                      ...f,
                      dateRange: (range as [Dayjs | null, Dayjs | null] | null) ?? null,
                    }))
                  }
                  showTime={{ format: "HH:mm" }}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12} lg={10}>
              <Form.Item
                label="Search (docId, project name, or changed field)"
                htmlFor="audit-search-input"
                style={{ marginBottom: 0 }}
              >
                <Input
                  id="audit-search-input"
                  placeholder="e.g. proj_abc123, status, Department of Test"
                  value={filters.search}
                  onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={24} lg={4} style={{ display: "flex", alignItems: "flex-end" }}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={fetchEntries}
                loading={loading}
                block
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      {error && (
        <Card>
          <Typography.Text type="danger">Failed to load audit log: {error}</Typography.Text>
        </Card>
      )}

      <Card>
        <Table<AuditLogEntry>
          rowKey="id"
          dataSource={entries}
          columns={columns}
          loading={loading}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          locale={{ emptyText: "No audit entries match the current filters." }}
          expandable={{
            expandedRowRender: (entry) => (
              <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                {entry.apexPrompt && (
                  <Collapse
                    items={[
                      {
                        key: "apex-prompt",
                        label: "Apex prompt",
                        children: (
                          <pre
                            style={{
                              margin: 0,
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-word",
                              fontSize: 12,
                              color: ditColors.primary,
                            }}
                          >
                            {entry.apexPrompt}
                          </pre>
                        ),
                      },
                    ]}
                  />
                )}
                {diffPanel(entry)}
                <div>
                  <Button size="small" onClick={() => handleCopy(entry)}>
                    Copy JSON
                  </Button>
                </div>
              </Space>
            ),
          }}
        />
      </Card>
    </Space>
  );
}
